import React, { useEffect, useState, useImperativeHandle } from "react";
import CryptoJS from "crypto-js";
import { Box, HStack, Text, useMediaQuery, VStack, Pressable } from "native-base";
import { uploadCloudIconSVG } from "../../utils/svg";
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from "../../utils/fonts";
import { MOBILE_VIEW_WIDTH } from "../../utils/screens";
import { COLORS } from "../../utils/colors";
import FileUploader from "../fileUpload";
import CustomModal from "../../components/modal";
import MobileModelSheet from "../../components/actionSheet";
import AttachmentsCards from "../attachmentCards";
import { LoaderAnimation } from "../../components/spinner";
import CustomButton from "../../components/button";
import { Toaster } from "../../components/toaster";
import { uploadFileAPI } from "../../api/apis/workflow";
import { uploadVendorDocs } from "../../api/apis/event";
import { statusMap, hasPermission } from "../../utils/permission";
import Dropdown from "../../components/dropdown";
import { executeAndWaitForCompletion } from "../../services/apiService.ts";
import { quotationCostBenchmarkingAPI, quotationAnalysisAPI } from "../../api/apis/quicklinks";
import DataTransformer from "../../utilsFunction/dataTransformer.ts";
import { uploadInvoiceFiles } from "../../services/benchmarking_file_upload_api.ts";
import { useHistory } from "react-router-dom";
import "./style.css";
import config from "../../utils/config";

const DocumentUploader = React.forwardRef(({ eventsDetail, getEventDetails, workflow }, ref) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [quatationFile, setQuatationFile] = useState(false);
  const [expenseFile, setExpenseFile] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState("");
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH,
  });
  const toaster = Toaster();
  const [selected, setSelected] = useState("null");
  const bytes = CryptoJS.AES.decrypt(
    sessionStorage.getItem("content"),
    config.userKey
  );
  const jsonString = bytes.toString(CryptoJS.enc.Utf8);
  const userInfo = JSON.parse(jsonString);
  const [forceRemount, setForceRemount] = useState(false);
  
  // Insights related state
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [selectedInsightsFiles, setSelectedInsightsFiles] = useState([]);
  const [selectedBenchmarkFile, setSelectedBenchmarkFile] = useState(null);
  const [isRunningInsights, setIsRunningInsights] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [insightsMode, setInsightsMode] = useState('quotation'); // 'quotation' or 'benchmark'
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [shimmerTextIndex, setShimmerTextIndex] = useState(0);
  const history = useHistory();

  // Array of shimmering texts to cycle through
  const shimmerTexts = [
    "Please wait while Param AI Studio is doing its magic✨",
    "Analyzing vendor data for cost benchmarking",
    "Processing market intelligence from multiple sources",
    "Refining data from vendors",
    "Analyzing reference data against these vendors",
    "Calculating cost variance metrics across categories",
    "Comparing your spend against market benchmarks",
    "Generating insights from procurement patterns",
    "Building comparative analysis charts",
    "Finalizing dashboard visualizations",
    "Almost ready - preparing your cost benchmark report"
  ];

  // Expose functions to parent component via ref
  useImperativeHandle(ref, () => ({
    openInsightsModal,
    openCostBenchmarking
  }));

  // Cycle through shimmer texts when loading
  useEffect(() => {
    let interval;
    if (isRunningInsights || showLoadingModal) {
      interval = setInterval(() => {
        setShimmerTextIndex((prevIndex) => 
          (prevIndex + 1) % shimmerTexts?.length
        );
      }, 6000); // Change text every 6 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunningInsights, showLoadingModal, shimmerTexts.length]);

  useEffect(() => {
    // Check if current user is a vendor/agency
    let userAgency = null;
    eventsDetail &&
      eventsDetail.agency &&
      eventsDetail.agency.forEach((item) => {
        if (item.emailId && Array.isArray(item.emailId) && item.emailId.includes(userInfo.email)) {
          if (item.status === "active") {
            setSelected(true);
            userAgency = item;
          } else if (item.status === "inactive") {
            setSelected(false);
            userAgency = item;
          }
        }
      });

    // Set up agency options for dropdown
    if (eventsDetail && eventsDetail.agency) {
      let options = [];
      
      if (userAgency || hasPermission("VENDOR")) {
        // If user is a vendor or belongs to an agency, show only their agency
        const targetAgency = userAgency || eventsDetail.agency.find(agency => 
          agency.emailId && Array.isArray(agency.emailId) && agency.emailId.includes(userInfo.email)
        );
        
        if (targetAgency) {
          options = [{
            value: targetAgency.id,
            label: targetAgency.name,
          }];
        }
      } else {
        // If user is not a vendor, show all agencies
        options = eventsDetail.agency.map((agency) => ({
          value: agency.id,
          label: agency.name,
        }));
      }
      
      setAgencyOptions(options);
      if (options.length > 0) {
        setSelectedAgency(options[0].value);
      }
    }

    // Update uploaded files and check if files exist
    let allFiles = [];
    let hasQuotationFile = false;
    let hasExpenseFile = false;
    
    eventsDetail &&
      eventsDetail.agency &&
      eventsDetail.agency.forEach((agency) => {
        // If user is a vendor or belongs to an agency, only show their agency files
        if ((userAgency && agency.id !== userAgency.id) || 
            (hasPermission("VENDOR") && !agency.emailId?.includes(userInfo.email))) {
          return;
        }
        
        if (agency.quotation_path && Object.keys(agency.quotation_path).length > 0) {
          hasQuotationFile = true;
          allFiles.push({
            name: agency.quotation_path.doc_type,
            size: agency.quotation_path.file_size,
            file_name: agency.quotation_path.file_name,
            time: agency.quotation_path.uploaded_time,
            quotation: true,
            agencyId: agency.id,
            agencyName: agency.name,
          });
        }
        if (agency.expense_path && Object.keys(agency.expense_path).length > 0) {
          hasExpenseFile = true;
          allFiles.push({
            name: agency.expense_path.doc_type,
            size: agency.expense_path.file_size,
            file_name: agency.expense_path.file_name,
            time: agency.expense_path.uploaded_time,
            quotation: false,
            agencyId: agency.id,
            agencyName: agency.name,
          });
        }
      });
    
    setUploadedFiles(allFiles);
    setQuatationFile(hasQuotationFile);
    setExpenseFile(hasExpenseFile);
  }, [eventsDetail]);

  const handleBrowseClick = () => {
    !MobileWindow && scrollToTop();
    setShowModal(true);
  };

  const closeModal = () => {
    setForceRemount(!forceRemount);
    setSelectedFiles([]);
    setShowModal(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleFilesSelect = (files) => {
    setSelectedFiles(files);
  };

  const handleAgencyChange = (value) => {
    setSelectedAgency(value);
  };



  // Insights related functions
  const handleInsightsFileSelect = (files) => {
    setSelectedInsightsFiles(files);
  };

  const handleBenchmarkFileSelect = (files) => {
    setSelectedBenchmarkFile(files[0] || null);
  };

  const openInsightsModal = () => {
    !MobileWindow && scrollToTop();
    setShowInsightsModal(true);
    setSelectedInsightsFiles([]);
    setSelectedBenchmarkFile(null);
    setInsightsError(null);
    setInsightsMode('quotation');
  };

  const openCostBenchmarking = async () => {
    !MobileWindow && scrollToTop();
    setShowLoadingModal(true);
    setIsRunningInsights(true);
    setInsightsError(null);
    setShimmerTextIndex(0);
    
    try {
      // Call the new quotation analysis API
      const response = await quotationAnalysisAPI(eventsDetail._id);
      
      if (response.error) {
        throw new Error(response.message || 'Failed to fetch quotation analysis');
      }
      const analysisData = response?.data?.data;
      
      if (!analysisData) {
        throw new Error('No analysis data received from API');
      }
      
      // Stop the loading state first
      setIsRunningInsights(false);
      
      // Close modal first
      setShowLoadingModal(false);
      
      // Add a delay to allow modal to close smoothly (fade out) before navigation
      // This prevents the modal border and close icon from flashing during navigation
      await new Promise(resolve => setTimeout(resolve, 400));
      
      // Navigate to insights page with new data structure
      history.push('/cost-benchmarking', {
        insightsData: {
          quotationAnalysis: analysisData,
          eventDetails: eventsDetail,
          mode: 'quotation-analysis',
          dataSource: 'abg-event-bench'
        }
      });
      
      // Show success message after navigation
      toaster("ABG Event Bench data loaded successfully!", "success", "success");
    } catch (error) {
      setInsightsError(error.message || 'Internal Server Error');
      setIsRunningInsights(false);
      setShowLoadingModal(false);
      toaster(error.message || 'Internal Server Error', "error", "error");
    }
  };

  const closeInsightsModal = () => {
    setShowInsightsModal(false);
    setSelectedInsightsFiles([]);
    setSelectedBenchmarkFile(null);
    setInsightsError(null);
    setIsRunningInsights(false);
    setInsightsMode('quotation');
  };

  const closeLoadingModal = () => {
    // Only allow closing if not actively running
    if (!isRunningInsights) {
      setShowLoadingModal(false);
      setIsRunningInsights(false);
      setInsightsError(null);
    }
  };

  const extractBenchmarkTabs = (apiResp) => {
    if (!apiResp || typeof apiResp !== 'object') return null;

    // Navigate to the correct path in the API response
    const tabContent = apiResp.response?.output?.content?.DataRead_001;
    
    if (!tabContent || !Array.isArray(tabContent) || tabContent.length === 0) {
      return null;
    }

    // Get content from the first item in the array
    const markdownContent = tabContent[0]?.content;
    
    if (!markdownContent || typeof markdownContent !== 'string') {
      return null;
    }

    // Extract JSON from markdown code block
    const jsonRegex = /```json\n([\s\S]*?)\n```/;
    const match = markdownContent.match(jsonRegex);
    
    if (!match || !match[1]) {
      return null;
    }

    try {
      // Parse the extracted JSON string
      const jsonData = JSON.parse(match[1]);
      return jsonData;
    } catch (e) {
      console.error("Failed to parse JSON from DataRead_001 content:", e);
      return null;
    }
  };

  const handleRunInsights = async () => {
    if (insightsMode === 'quotation' && selectedInsightsFiles.length === 0) {
      toaster("Please add quotation files to get insights", "error", "error");
      return;
    }

    if (insightsMode === 'benchmark' && !selectedBenchmarkFile) {
      toaster("Please add benchmark file to get insights", "error", "error");
      return;
    }

    setIsRunningInsights(true);
    setInsightsError(null);
    setShimmerTextIndex(0);

    try {
      if (insightsMode === 'quotation') {
        // Handle quotation insights
        await executeAndWaitForCompletion(
          selectedInsightsFiles,
          {},
          (msg) => {
            if (msg.status === 'failed' || msg.status === 'error') {
              setIsRunningInsights(false);
              setInsightsError(msg.message || 'Insights generation failed.');
              toaster(msg.message || 'Insights generation failed.', "error", "error");
            }
          },
          (sessionData) => {
            // Transform data for the dashboard
            const transformedDataRaw = DataTransformer.transformSessionData(sessionData);
            const allowedTabs = ['Vendor to Vendor', 'Vendor to Benchmark'];
            const filteredTabs = transformedDataRaw.tabs.filter((tab) => allowedTabs.includes(tab));
            const transformedData = {
              ...transformedDataRaw,
              tabs: filteredTabs
            };
            
            setIsRunningInsights(false);
            closeInsightsModal();
            toaster("Insights generated successfully!", "success", "success");
            
            // Navigate to insights page with data
            history.push('/cost-benchmarking', {
              insightsData: {
                sessionData: sessionData,
                transformedData: transformedData,
                eventDetails: eventsDetail,
                mode: 'quotation',
                dataSource: 'file-upload'
              }
            });
          }
        );
      } else {
        // Handle benchmark insights
        const raw = await uploadInvoiceFiles(selectedBenchmarkFile);        
        const benchmarkData = extractBenchmarkTabs(raw);
        
        if (!benchmarkData) {
          setInsightsError('Could not find DataRead_001 tab in the response. The file may have a different structure than expected.');
          setIsRunningInsights(false);
          toaster('Could not process benchmark file', "error", "error");
          return;
        }
        
        setIsRunningInsights(false);
        closeInsightsModal();
        toaster("Benchmark data processed successfully!", "success", "success");
        
        // Navigate to insights page with benchmark data
        history.push('/cost-benchmarking', {
          insightsData: {
            benchmarkData: benchmarkData,
            eventDetails: eventsDetail,
            mode: 'benchmark',
            dataSource: 'file-upload'
          }
        });
      }
    } catch (err) {
      setInsightsError(err.message || 'Failed to generate insights');
      setIsRunningInsights(false);
      toaster(err.message || 'Failed to generate insights', "error", "error");
    }
  };

  const getStartDateFormat = (dateString) => {
    const date = new Date(dateString);
    function padTo2Digits(num) {
      return num.toString().padStart(2, "0");
    }
    function formatDate(date) {
      return (
        [
          date.getFullYear(),
          padTo2Digits(date.getMonth() + 1),
          padTo2Digits(date.getDate()),
        ].join("-") +
        "T" +
        [
          padTo2Digits(date.getHours()),
          padTo2Digits(date.getMinutes()),
          padTo2Digits(date.getSeconds()),
        ].join(":") +
        "." +
        "616838+00:00"
      );
    }
    const formattedDate = formatDate(date);

    return formattedDate;
  };

  const uploadFilesToServer = async (data) => {
    let docs = [];
    let res = await uploadFileAPI(data, "file", "quotation");

    res.map((item) => {
      if (item.error) {
        toaster("Upload file failed", "error", "error");
        return;
      }
      if (item.data.data.data) {
        docs.push({
          doc_type: item.data.data.data.doc_type,
          file_size: item.data.data.data.file_size.toString(),
          file_name: item.data.data.data.file_name,
          uploaded_time: getStartDateFormat(new Date()),
        });
      }
    });

    return docs;
  };

  const onDeleteFile = async (agencyId = null) => {
    LoaderAnimation.show();
    
    // For vendors, ensure they can only delete their own agency files
    if (hasPermission("VENDOR") && selected === "null") {
      const userAgency = eventsDetail.agency.find(agency => 
        agency.emailId && Array.isArray(agency.emailId) && agency.emailId.includes(userInfo.email)
      );
      if (userAgency && agencyId !== userAgency.id) {
        toaster("You can only delete quotations for your own agency", "error", "error");
        LoaderAnimation.hide();
        return;
      }
    }
    
    let payload = { ...eventsDetail };
    let updatedAgency = [];
    
    payload.agency.map((item, ind) => {
      if (selected === "null") {
        // Quotation deletion - use provided agencyId or current user's agency
        const targetAgencyId = agencyId || (item.emailId && Array.isArray(item.emailId) && item.emailId.includes(userInfo.email) ? item.id : null);
        if (item.id === targetAgencyId) {
          updatedAgency.push({ ...item, quotation_path: {} });
        } else {
          updatedAgency.push(item);
        }
      } else if (item.emailId && Array.isArray(item.emailId) && item.emailId.includes(userInfo.email)) {
        // Expense deletion - use current user's agency
        if (selected === true) {
          updatedAgency.push({ ...item, expense_path: {} });
        } else {
          updatedAgency.push(item);
        }
      } else {
        updatedAgency.push(item);
      }
    });

    payload.agency = updatedAgency;
    payload.workflow_instance_id = payload["_id"];
    delete payload["_id"];
    delete payload["notification_for"];

    let postData = {
      instance: [payload],
    };
    let res = await uploadVendorDocs(
      workflow.event._id,
      eventsDetail.current_step,
      "ADD_DOCUMENT",
      postData
    );
    if (res.error) {
      toaster(res.message, "error", "error");
      LoaderAnimation.hide();
      return;
    } else {
      if (selected === "null") {
        setQuatationFile(false);
      }
      if (selected === true) {
        setExpenseFile(false);
      }

      toaster("Deleted Successfully", "success", "success");
      await getEventDetails();
      closeModal();
    }
    LoaderAnimation.hide();
  };

  const modalFooter = (
    <HStack width={MobileWindow && "100%"}>
      <Box width={MobileWindow && "100%"}>
        <CustomButton
          onPress={async () => {
            LoaderAnimation.show();
            if (selectedFiles.length === 0) {
              toaster("Please add file to upload", "error", "error");
              LoaderAnimation.hide();
              return;
            }
            
            // For quotation upload, validate agency selection
            if (selected === "null" && !selectedAgency) {
              toaster("Please select an agency", "error", "error");
              LoaderAnimation.hide();
              return;
            }

            // For vendors, ensure they can only upload for their own agency
            if (hasPermission("VENDOR") && selected === "null") {
              const userAgency = eventsDetail.agency.find(agency => 
                agency.emailId && Array.isArray(agency.emailId) && agency.emailId.includes(userInfo.email)
              );
              if (userAgency && selectedAgency !== userAgency.id) {
                toaster("You can only upload quotations for your own agency", "error", "error");
                LoaderAnimation.hide();
                return;
              }
            }

            let payload = { ...eventsDetail };
            let docs = await uploadFilesToServer(selectedFiles);

            let updatedAgency = [];
            let text = "";
            
            payload.agency.map((item, ind) => {
              if (selected === "null") {
                // Quotation upload - use selected agency
                if (item.id === selectedAgency) {
                  text = "quotation";
                  updatedAgency.push({ ...item, quotation_path: docs[0] });
                } else {
                  updatedAgency.push(item);
                }
              } else if (item.emailId && Array.isArray(item.emailId) && item.emailId.includes(userInfo.email)) {
                // Expense upload - use current user's agency
                if (
                  statusMap.indexOf(eventsDetail.status) >= 2 &&
                  selected === true
                ) {
                  text = "expenses";
                  updatedAgency.push({ ...item, expense_path: docs[0] });
                } else {
                  updatedAgency.push(item);
                }
              } else {
                updatedAgency.push(item);
              }
            });

            payload.notification_for = text;
            payload.agency = updatedAgency;
            payload.workflow_instance_id = payload["_id"];
            delete payload["_id"];

            let postData = {
              instance: [payload],
            };

            let res = await uploadVendorDocs(
              workflow.event._id,
              eventsDetail.current_step,
              "ADD_DOCUMENT",
              postData
            );
            if (res.error) {
              toaster(res.message, "error", "error");
              LoaderAnimation.hide();
              return;
            } else {
              toaster("Uploaded Successfully", "success", "success");
              await getEventDetails();
              closeModal();
            }
            LoaderAnimation.hide();
          }}
          text="Upload"
          color={COLORS.WHITE_COLOR}
        />
      </Box>
    </HStack>
  );

  const insightsModalFooter = (
    <HStack width={MobileWindow && "100%"}>
      <Box width={MobileWindow && "100%"}>
        <CustomButton
          onPress={handleRunInsights}
          text={
            isRunningInsights 
              ? "Processing..." 
              : insightsMode === 'quotation'
                ? selectedInsightsFiles.length > 0 
                  ? `Analyse Cost Benchmarking (${selectedInsightsFiles.length} files)`
                  : "Analyse Cost Benchmarking"
                : selectedBenchmarkFile
                  ? "Analyse Cost Benchmarking"
                  : "Analyse Cost Benchmarking"
          }
          color={COLORS.WHITE_COLOR}
          disabled={
            isRunningInsights || 
            (insightsMode === 'quotation' && selectedInsightsFiles.length === 0) ||
            (insightsMode === 'benchmark' && !selectedBenchmarkFile)
          }
        />
      </Box>
    </HStack>
  );

  return (
    <Box width={"100%"}>
      <HStack
      display={'flex'}
      flexDirection={'column'}
      alignItems={'flex-start'}
        space={2}
        direction='column'
        width={"100%"}
      >
        {statusMap.indexOf(eventsDetail.status) <= 3 &&
          selected === "null" &&
          (!quatationFile || !hasPermission("VENDOR")) && (
            <div style={{
              marginTop: '8px'
            }}>
              <p
                className="upload_file"
                style={{ minWidth: MobileWindow ? undefined : "344px" }}
              >
                <span style={{ marginRight: "10px", marginTop: "8px" }}>
                  {uploadCloudIconSVG}
                </span>
                <label
                  htmlFor="fileInput"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  {!MobileWindow && (
                    <span style={{ color: "#999", marginRight: "10px" }}>
                      Drop items here or
                    </span>
                  )}
                  <span
                    style={{
                      fontWeight: COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT,
                      cursor: "pointer",
                    }}
                    onClick={handleBrowseClick}
                  >
                    Browse File
                  </span>
                </label>
              </p>
            </div>
          )}

        <div
          style={{
            width: "100%",
            display: MobileWindow ? "flex" : "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(30%, 1fr))`,
            gap: "30px",
            flexWrap: "wrap",
            marginTop: "16px",
            height: "fit-content",
          }}
        >
          {uploadedFiles.map((file, index) => (
            <AttachmentsCards
              key={`${file.agencyId}-${file.quotation ? 'quotation' : 'expense'}-${index}`}
              fileName={`${file.name} (${file.agencyName})`}
              fileSize={file.size}
              path={config.bucketPath + file.file_name}
              timeline={new Date().toISOString()}
              attachments={true}
              color={COLORS.WHITE_COLOR}
              deleteIcon={
                (selected === "null" &&
                  statusMap.indexOf(eventsDetail.status) <= 3 &&
                  file.quotation) ||
                (statusMap.indexOf(eventsDetail.status) >= 2 && !file.quotation)
              }
              translateY={"-24px"}
              wallet={true}
              onDeleteFile={() => onDeleteFile(file.agencyId)}
              width="76%"
              quotation
            />
          ))}
        </div>
      </HStack>

      {!MobileWindow ? (
        <CustomModal
          isOpen={showModal}
          onClose={closeModal}
          header={
            selected === "null" ? (
              <Text>Upload Quotation</Text>
            ) : (
              <Text>Upload Expense</Text>
            )
          }
          body={
            <VStack space={4}>
              {selected === "null" && (
                <Dropdown
                  label="Select Agency"
                  name="agency"
                  selectedValues={selectedAgency}
                  setSelectedValues={handleAgencyChange}
                  placeholder="Agency"
                  dropItems={agencyOptions}
                  important={true}
                  forceRemount={forceRemount}
                />
              )}
              <FileUploader
                files={selectedFiles}
                show={true}
                setFiles={handleFilesSelect}
                accept={["pdf"]}
                types=".pdf"
                forceRemount={forceRemount}
                // images={true}
              />
            </VStack>
          }
          footer={modalFooter}
          width={MobileWindow ? "100%" : "70%"}
        />
      ) : (
        <MobileModelSheet
          isOpen={showModal}
          onClose={closeModal}
          header={
            selected === "null" ? (
              <Text>Upload Quotation</Text>
            ) : (
              <Text>Upload Expense</Text>
            )
          }
          body={
            <VStack space={4}>
              {selected === "null" && (
                <Dropdown
                  label="Select Agency"
                  name="agency"
                  selectedValues={selectedAgency}
                  setSelectedValues={handleAgencyChange}
                  placeholder="Agency"
                  dropItems={agencyOptions}
                  important={true}
                  forceRemount={forceRemount}
                />
              )}
              <FileUploader
                files={selectedFiles}
                show={true}
                setFiles={handleFilesSelect}
                accept={["pdf"]}
                types=".pdf"
                // images={true}
                forceRemount={forceRemount}
              />
            </VStack>
          }
          footer={modalFooter}
        />
      )}

      {/* Insights Modal */}
      {!MobileWindow ? (
        <CustomModal
          isOpen={showInsightsModal}
          onClose={closeInsightsModal}
          header={<Text>Event Benchmarking</Text>}
          body={
            <VStack space={4}>
              {/* Mode Toggle */}
              <HStack space={4} alignItems="center">
                <Text fontSize="sm" fontWeight="medium">Mode:</Text>
                <HStack space={2}>
                  <Pressable
                    onPress={() => setInsightsMode('quotation')}
                    bg={insightsMode === 'quotation' ? COLORS.TABS_ACTIVE_BORDER_COLOR : 'gray.200'}
                    px={4}
                    py={2}
                    borderRadius="md"
                  >
                    <Text 
                      fontSize="sm" 
                      color={insightsMode === 'quotation' ? 'white' : 'gray.700'}
                      fontWeight="medium"
                    >
                      Quotations
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={() => setInsightsMode('benchmark')}
                    bg={insightsMode === 'benchmark' ? COLORS.TABS_ACTIVE_BORDER_COLOR : 'gray.200'}
                    px={4}
                    py={2}
                    borderRadius="md"
                  >
                    <Text 
                      fontSize="sm" 
                      color={insightsMode === 'benchmark' ? 'white' : 'gray.700'}
                      fontWeight="medium"
                    >
                      Invoices
                    </Text>
                  </Pressable>
                </HStack>
              </HStack>

              {insightsMode === 'quotation' ? (
                <>
                  <Text fontSize="sm" color="gray.600">
                    Upload multiple quotation files to generate insights and cost analysis
                  </Text>
                  <FileUploader
                    files={selectedInsightsFiles}
                    show={true}
                    setFiles={handleInsightsFileSelect}
                    accept={["pdf"]}
                    types=".pdf"
                    multiple={true}
                    forceRemount={forceRemount}
                  />
                  {selectedInsightsFiles.length > 0 && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Selected Files ({selectedInsightsFiles.length}):
                      </Text>
                      <Box maxH="100px" overflowY="auto" borderWidth={1} borderColor="gray.200" borderRadius="md" p={2}>
                        {selectedInsightsFiles.map((file, index) => (
                          <Text key={index} fontSize="xs" color="gray.600" mb={1}>
                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          </Text>
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Text fontSize="sm" color="gray.600">
                    Upload a invoice file to analyze cost data
                  </Text>
                  <FileUploader
                    files={selectedBenchmarkFile ? [selectedBenchmarkFile] : []}
                    show={true}
                    setFiles={handleBenchmarkFileSelect}
                    accept={["pdf", "xlsx", "xls"]}
                    types=".pdf,.xlsx,.xls"
                    multiple={false}
                    forceRemount={forceRemount}
                  />
                  {selectedBenchmarkFile && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Selected File:
                      </Text>
                      <Box borderWidth={1} borderColor="gray.200" borderRadius="md" p={2}>
                        <Text fontSize="xs" color="gray.600">
                          {selectedBenchmarkFile.name} ({(selectedBenchmarkFile.size / 1024).toFixed(1)} KB)
                        </Text>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {insightsError && (
                <Text color="red.500" fontSize="sm">
                  {insightsError}
                </Text>
              )}
              {isRunningInsights && (
                <HStack space={2} alignItems="center">
                  <Box
                    size={4}
                    borderWidth={2}
                    borderColor="orange.500"
                    borderTopColor="transparent"
                    borderRadius="full"
                    style={{
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  <Box
                    position="relative"
                    fontSize="sm"
                    color="orange.500"
                    fontWeight="500"
                    style={{
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(239, 175, 130, 0.8) 50%, transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite linear',
                        pointerEvents: 'none'
                      }}
                    />
                    {shimmerTexts[shimmerTextIndex]}
                  </Box>
                </HStack>
              )}
            </VStack>
          }
          footer={insightsModalFooter}
          width={MobileWindow ? "100%" : "70%"}
        />
      ) : (
        <MobileModelSheet
          isOpen={showInsightsModal}
          onClose={closeInsightsModal}
          header={<Text>Event Benchmarking</Text>}
          body={
            <VStack space={4}>
              {/* Mode Toggle */}
              <HStack space={4} alignItems="center">
                <Text fontSize="sm" fontWeight="medium">Mode:</Text>
                <HStack space={2}>
                  <Pressable
                    onPress={() => setInsightsMode('quotation')}
                    bg={insightsMode === 'quotation' ? COLORS.TABS_ACTIVE_BORDER_COLOR : 'gray.200'}
                    px={4}
                    py={2}
                    borderRadius="md"
                  >
                    <Text 
                      fontSize="sm" 
                      color={insightsMode === 'quotation' ? 'white' : 'gray.700'}
                      fontWeight="medium"
                    >
                      Quotations
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={() => setInsightsMode('benchmark')}
                    bg={insightsMode === 'benchmark' ? COLORS.TABS_ACTIVE_BORDER_COLOR : 'gray.200'}
                    px={4}
                    py={2}
                    borderRadius="md"
                  >
                    <Text 
                      fontSize="sm" 
                      color={insightsMode === 'benchmark' ? 'white' : 'gray.700'}
                      fontWeight="medium"
                    >
                      Invoices
                    </Text>
                  </Pressable>
                </HStack>
              </HStack>

              {insightsMode === 'quotation' ? (
                <>
                  <Text fontSize="sm" color="gray.600">
                    Upload multiple quotation files to generate insights and cost analysis
                  </Text>
                  <FileUploader
                    files={selectedInsightsFiles}
                    show={true}
                    setFiles={handleInsightsFileSelect}
                    accept={["pdf"]}
                    types=".pdf"
                    multiple={true}
                    forceRemount={forceRemount}
                  />
                  {selectedInsightsFiles.length > 0 && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Selected Files ({selectedInsightsFiles.length}):
                      </Text>
                      <Box maxH="100px" overflowY="auto" borderWidth={1} borderColor="gray.200" borderRadius="md" p={2}>
                        {selectedInsightsFiles.map((file, index) => (
                          <Text key={index} fontSize="xs" color="gray.600" mb={1}>
                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          </Text>
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Text fontSize="sm" color="gray.600">
                    Upload a invoice file to analyze cost data
                  </Text>
                  <FileUploader
                    files={selectedBenchmarkFile ? [selectedBenchmarkFile] : []}
                    show={true}
                    setFiles={handleBenchmarkFileSelect}
                    accept={["pdf", "xlsx", "xls"]}
                    types=".pdf,.xlsx,.xls"
                    multiple={false}
                    forceRemount={forceRemount}
                  />
                  {selectedBenchmarkFile && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Selected File:
                      </Text>
                      <Box borderWidth={1} borderColor="gray.200" borderRadius="md" p={2}>
                        <Text fontSize="xs" color="gray.600">
                          {selectedBenchmarkFile.name} ({(selectedBenchmarkFile.size / 1024).toFixed(1)} KB)
                        </Text>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {insightsError && (
                <Text color="red.500" fontSize="sm">
                  {insightsError}
                </Text>
              )}
              {isRunningInsights && (
                <HStack space={2} alignItems="center">
                  <Box
                    size={4}
                    borderWidth={2}
                    borderColor="orange.500"
                    borderTopColor="transparent"
                    borderRadius="full"
                    animation="spin 1s linear infinite"
                  />
                  <Box
                    position="relative"
                    fontSize="sm"
                    color="orange.500"
                    fontWeight="500"
                    style={{
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(239, 175, 130, 0.8) 50%, transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite linear',
                        pointerEvents: 'none'
                      }}
                    />
                    {shimmerTexts[shimmerTextIndex]}
                  </Box>
                </HStack>
              )}
            </VStack>
          }
          footer={insightsModalFooter}
        />
      )}

      {/* Loading Modal for Cost Benchmarking */}
      {!MobileWindow ? (
        <CustomModal
          isOpen={showLoadingModal}
          onClose={isRunningInsights ? () => {} : closeLoadingModal}
          header={<Text>ABG Event Bench</Text>}
          body={
            <VStack space={4} alignItems="center" py={4}>
              {insightsError ? (
                <Text color="red.500" fontSize="sm" textAlign="center">
                  {insightsError}
                </Text>
              ) : (
                <HStack space={3} alignItems="center">
                  <Box
                    size={6}
                    borderWidth={3}
                    borderColor="orange.500"
                    borderTopColor="transparent"
                    borderRadius="full"
                    style={{
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  <Box
                    position="relative"
                    fontSize="md"
                    color="orange.500"
                    fontWeight="500"
                    style={{
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(239, 175, 130, 0.8) 50%, transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite linear',
                        pointerEvents: 'none'
                      }}
                    />
                    {shimmerTexts[shimmerTextIndex]}
                  </Box>
                </HStack>
              )}
            </VStack>
          }
          width={MobileWindow ? "100%" : "50%"}
        />
      ) : (
        <MobileModelSheet
          isOpen={showLoadingModal}
          onClose={isRunningInsights ? () => {} : closeLoadingModal}
          header={<Text>ABG Event Bench</Text>}
          body={
            <VStack space={4} alignItems="center" py={4}>
              {insightsError ? (
                <Text color="red.500" fontSize="sm" textAlign="center">
                  {insightsError}
                </Text>
              ) : (
                <HStack space={3} alignItems="center">
                  <Box
                    size={6}
                    borderWidth={3}
                    borderColor="orange.500"
                    borderTopColor="transparent"
                    borderRadius="full"
                    style={{
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  <Box
                    position="relative"
                    fontSize="md"
                    color="orange.500"
                    fontWeight="500"
                    style={{
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(239, 175, 130, 0.8) 50%, transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite linear',
                        pointerEvents: 'none'
                      }}
                    />
                    {shimmerTexts[shimmerTextIndex]}
                  </Box>
                </HStack>
              )}
            </VStack>
          }
        />
      )}
    </Box>
  );
});

export default DocumentUploader;
